import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { userService, workshopService } from "../services/dataService";
import { authService } from "../services/authService";
import { getRoles } from "../services/api";
import { User, Workshop, EmployeeLoginResult } from "../types";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import LoginCredentialsModal from "../components/LoginCredentialsModal";
import PageLoader from "../components/PageLoader";
import "./PurchaseOrders.css";
import "./UserManagement.css";
import { usePermissions } from "../context/PermissionContext";
import { Navigate } from "react-router-dom";

const roles = [
  { value: "Manager", label: "Müdür" },
  { value: "Sekreterya", label: "Sekreterya" },
  { value: "Modelist", label: "Modelist" },
  { value: "Üretim", label: "Üretim" },
  { value: "Muhasebe", label: "Muhasebe" },
];

// Avatar tonu (kullanıcıya göre deterministik; po-mark --po-hue)
const avatarHue = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % 360;
};
const initials = (first: string, last: string) =>
  `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";

// API hataları düz metin (BadRequest("…")) ya da { message | title } gelir
const errorText = (error: any): string => {
  const d = error?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (d?.message) return d.message;
  if (d?.title) return d.title;
  if (error?.response?.status === 403) return "Bu işlem için yetkiniz yok.";
  return error?.message || "Bilinmeyen hata";
};

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  workshopIds: [] as string[],
  isActive: true,
  password: "",
  // Yeni kullanıcı: varsayılan geçici şifre üretilir ve postalanır; yönetici isterse kendisi belirler
  passwordMode: "generate" as "generate" | "manual",
};

type StatusFilter = "" | "active" | "first" | "passive";

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="15" r="4" /><path d="m10.85 12.15 8.65-8.65M17 6l2 2M14 9l2 2" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18 14.5a6.5 6.5 0 0 1 3.5 5.5" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
};

// İK modülü lisanslıysa kullanıcı yönetimi personel kartına taşındı (kart = kullanıcı): bu ekran yalnız İK'sız kurulumlarda kalır.
const UserManagement: React.FC = () => {
  const { hasModule, loaded } = usePermissions();
  if (loaded && hasModule("hr")) return <Navigate to="/hr?tab=employees" replace />;
  return <UserManagementLegacy />;
};

const UserManagementLegacy: React.FC = () => {
  const { has } = usePermissions(); // yetki matrisi
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [apiRoles, setApiRoles] = useState<{ roleId: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  // Geçici şifre bir kez burada gösterilir (hesap açılışı ya da sıfırlama)
  const [loginResult, setLoginResult] = useState<{ who: string; r: EmployeeLoginResult; reset?: boolean } | null>(null);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetting, setResetting] = useState(false); // istek sürerken anahtar düğmeleri kapalı (çift sıfırlama olmasın)

  // Liste süzgeci (yalnız istemci tarafı)
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
  }>({ isOpen: false, userId: null, userName: "" });

  const isManager = currentUser?.role === "Manager";
  const isEdit = !!editingUser;
  const canUpdate = has("user-management:update");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, workshopsData, rolesRes] = await Promise.all([
        userService.getAll(),
        workshopService.getAll(),
        getRoles(),
      ]);
      setUsers(usersData);
      setWorkshops(workshopsData);
      setApiRoles(rolesRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenirken hata oluştu",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingUser(null);
    setFormData({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      workshopIds: user.workshops?.map((w) => w.workshopId) || [],
      isActive: user.isActive,
      password: "",
      passwordMode: "generate" as const,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({ ...emptyForm });
  };

  // Modal: Esc kapatır (capture; alttaki dinleyicilere ulaşmaz)
  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) {
        e.stopPropagation();
        setIsModalOpen(false);
        setEditingUser(null);
        setFormData({ ...emptyForm });
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isModalOpen, saving]);

  const toggleWorkshop = (workshopId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      workshopIds: checked
        ? [...prev.workshopIds, workshopId]
        : prev.workshopIds.filter((id) => id !== workshopId),
    }));
  };

  const toggleAllWorkshops = () => {
    setFormData((prev) => ({
      ...prev,
      workshopIds:
        prev.workshopIds.length === workshops.length
          ? []
          : workshops.map((w) => w.workshopId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      if (!editingUser) return;
      try {
        setSaving(true);
        const updateData: any = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive,
        };
        if (formData.workshopIds.length > 0) {
          updateData.workshopIds = formData.workshopIds;
        }
        if (formData.password && formData.password.trim().length >= 6) {
          updateData.password = formData.password.trim();
        }
        await userService.update(editingUser.userId, updateData);
        setAlertModal({
          isOpen: true,
          message: "Kullanıcı başarıyla güncellendi",
          type: "success",
        });
        closeModal();
        loadData();
      } catch (error) {
        console.error("Error updating user:", error);
        setAlertModal({
          isOpen: true,
          message: "Kullanıcı güncellenirken hata oluştu",
          type: "error",
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    // Yeni kullanıcı: geçici şifre modunda şifre gönderilmez, sunucu üretip postalar
    const manual = formData.passwordMode === "manual";
    if (manual && (!formData.password || formData.password.trim().length < 6)) {
      setAlertModal({
        isOpen: true,
        message: "Şifre en az 6 karakter olmalıdır",
        type: "warning",
      });
      return;
    }
    try {
      setSaving(true);
      const createData = {
        username: formData.email,
        email: formData.email,
        password: manual ? formData.password.trim() : undefined,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        workshopIds:
          formData.workshopIds.length > 0 ? formData.workshopIds : undefined,
        isActive: true,
        createdBy: currentUser?.userId || "unknown",
      };
      const who = `${formData.firstName} ${formData.lastName}`.trim();
      const created = await authService.register(createData);
      closeModal();
      loadData();
      if (created?.login) {
        setLoginResult({ who, r: created.login });
      } else {
        setAlertModal({
          isOpen: true,
          message: `Kullanıcı "${who}" başarıyla oluşturuldu!`,
          type: "success",
        });
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı oluşturulamadı: " + errorText(error),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Yeni geçici şifre: eski şifre geçersiz olur, kişi ilk girişte yenisini belirler; posta gider, şifre bir kez gösterilir
  const handleConfirmReset = async () => {
    const u = resetTarget;
    if (!u || resetting) return;
    setResetTarget(null);
    setResetting(true);
    try {
      const r = await userService.resetLogin(u.userId);
      setLoginResult({ who: `${u.firstName} ${u.lastName}`.trim(), r, reset: true });
      loadData();
    } catch (error: any) {
      setAlertModal({
        isOpen: true,
        message: "Geçici şifre üretilemedi: " + errorText(error),
        type: "error",
      });
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = (user: User) => {
    setConfirmModal({
      isOpen: true,
      userId: user.userId,
      userName: `${user.firstName} ${user.lastName}`,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmModal.userId) return;
    try {
      await userService.delete(confirmModal.userId);
      setAlertModal({
        isOpen: true,
        message: `Kullanıcı "${confirmModal.userName}" başarıyla silindi`,
        type: "success",
      });
      setConfirmModal({ isOpen: false, userId: null, userName: "" });
      loadData();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      setAlertModal({
        isOpen: true,
        message:
          "Kullanıcı silinirken hata oluştu: " + errorText(error),
        type: "error",
      });
      setConfirmModal({ isOpen: false, userId: null, userName: "" });
    }
  };

  const getRoleLabel = (role: string) =>
    roles.find((r) => r.value === role)?.label || role;

  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.isActive).length;
  const passiveUsers = totalUsers - activeUsers;
  const firstLoginUsers = users.filter((u) => u.isActive && u.isFirstLogin).length;
  const workshopRequired =
    formData.role === "Üretim" || formData.role === "Modelist";

  // Rol çipleri: listede gerçekten bulunan roller (sayılarıyla)
  const roleCounts = useMemo(() => {
    const m = new Map<string, number>();
    users.forEach((u) => m.set(u.role, (m.get(u.role) || 0) + 1));
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [users]);

  const displayUsers = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    return users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter === "active" && !u.isActive) return false;
      if (statusFilter === "passive" && u.isActive) return false;
      if (statusFilter === "first" && !(u.isActive && u.isFirstLogin)) return false;
      if (!s) return true;
      const hay = [
        `${u.firstName} ${u.lastName}`,
        u.email || "",
        getRoleLabel(u.role),
        (u.workshops || []).map((w) => w.name).join(" "),
      ].join(" ").toLocaleLowerCase("tr");
      return hay.includes(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, q, roleFilter, statusFilter]);

  const filterOn = !!(q || roleFilter || statusFilter);
  const resetFilters = () => { setQ(""); setRoleFilter(""); setStatusFilter(""); };
  const toggleStatus = (s: StatusFilter) => setStatusFilter((cur) => (cur === s ? "" : s));

  if (loading && users.length === 0) return <PageLoader message="Kullanıcılar yükleniyor..." />;

  return (
    <div className="po-page um">
      <header className="po-head">
        <div>
          <span className="po-kicker">Yönetim</span>
          <h1>Kullanıcı yönetimi</h1>
          <p>Sistem kullanıcılarını, rollerini ve atölye erişimlerini yönetin; geçici şifre üretip gönderin.</p>
        </div>
        {has("user-management:create") && (
          <div className="po-head-actions">
            <button type="button" className="po-btn primary" onClick={openAdd}>{I.plus} Yeni kullanıcı</button>
          </div>
        )}
      </header>

      <section className="po-kpis">
        <div className="po-kpi navy"><span className="l">Toplam kullanıcı</span><span className="v">{totalUsers}</span><span className="s"><b>{roleCounts.length}</b> farklı rol</span></div>
        <button type="button" className={`po-kpi ok ${statusFilter === "active" ? "on" : ""}`} aria-pressed={statusFilter === "active"} onClick={() => toggleStatus("active")}>
          <span className="l">Aktif</span><span className="v">{activeUsers}</span><span className="s">giriş yapabilir · süzmek için tıklayın</span>
        </button>
        <button type="button" className={`po-kpi um-kpi-warn ${statusFilter === "first" ? "on" : ""}`} aria-pressed={statusFilter === "first"} onClick={() => toggleStatus("first")}>
          <span className="l">İlk girişini bekleyen</span><span className="v">{firstLoginUsers}</span><span className="s">geçici şifreyle henüz girmedi</span>
        </button>
        <button type="button" className={`po-kpi ${statusFilter === "passive" ? "on" : ""}`} aria-pressed={statusFilter === "passive"} onClick={() => toggleStatus("passive")}>
          <span className="l">Pasif</span><span className="v">{passiveUsers}</span><span className="s">sisteme giriş yapamaz</span>
        </button>
      </section>

      <section className={`po-card ${loading ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">
            {I.search}
            <input placeholder="Ad, e-posta, rol veya atölye ara" aria-label="Kullanıcı ara" value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button type="button" className="po-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}
          </div>
          {roleCounts.length > 1 && (
            <div className="po-chipgroup" role="group" aria-label="Rol">
              <button type="button" className={roleFilter === "" ? "on" : ""} aria-pressed={roleFilter === ""} onClick={() => setRoleFilter("")}>Tümü<i>{totalUsers}</i></button>
              {roleCounts.map(([r, n]) => (
                <button type="button" key={r} className={roleFilter === r ? "on" : ""} aria-pressed={roleFilter === r} onClick={() => setRoleFilter(roleFilter === r ? "" : r)}>{getRoleLabel(r)}<i>{n}</i></button>
              ))}
            </div>
          )}
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
        </div>
        <div className="po-card-head">
          <span><b>{displayUsers.length}</b> kullanıcı{filterOn && ` · toplam ${totalUsers}`}</span>
          {canUpdate && <span className="po-hint po-hide-sm">Düzenlemek için satıra tıklayın</span>}
        </div>

        {displayUsers.length === 0 ? (
          users.length === 0 ? (
            <div className="po-blank"><span className="po-blank-icon">{I.users}</span><b>Henüz kullanıcı yok</b>İlk kullanıcıyı ekleyin; geçici şifresi e-postasına gönderilir.
              {has("user-management:create") && <button type="button" className="po-btn primary" onClick={openAdd}>{I.plus} Yeni kullanıcı</button>}
            </div>
          ) : (
            <div className="po-blank"><span className="po-blank-icon">{I.search}</span><b>Süzgeçle eşleşen kullanıcı yok</b>Aramayı ya da rol / durum süzgecini değiştirin.
              <button type="button" className="po-btn" onClick={resetFilters}>Süzgeci sıfırla</button>
            </div>
          )
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead>
                <tr>
                  <th>Kullanıcı</th>
                  <th className="po-hide-sm">Rol</th>
                  <th className="po-hide-md">Atölye</th>
                  <th>Durum</th>
                  <th className="act" aria-label="İşlemler" />
                </tr>
              </thead>
              <tbody>
                {displayUsers.map((user) => {
                  const isSelf = user.userId === currentUser?.userId;
                  const name = `${user.firstName} ${user.lastName}`.trim();
                  return (
                    <tr
                      key={user.userId}
                      className={[!canUpdate ? "static" : "", !user.isActive ? "off" : ""].join(" ")}
                      onClick={canUpdate ? () => openEdit(user) : undefined}
                    >
                      <td>
                        <div className="po-row">
                          <span className={`po-mark ${!user.isActive ? "off" : ""}`} style={{ ["--po-hue" as any]: avatarHue(user.userId || user.email) }} aria-hidden="true">
                            {initials(user.firstName, user.lastName)}
                          </span>
                          <div className="um-who">
                            <div className="po-name">{name}{isSelf && <span className="um-you">Siz</span>}</div>
                            <div className="po-sub">{user.email}</div>
                            {user.workshops && user.workshops.length > 0 && (
                              <div className="po-sub po-only-sm um-ws-sm">{user.workshops.map((w) => w.name).join(", ")}</div>
                            )}
                            <div className="po-only-sm um-role-sm"><span className={`po-badge ${user.role === "Manager" ? "info" : "muted"}`}>{getRoleLabel(user.role)}</span></div>
                          </div>
                        </div>
                      </td>
                      <td className="po-hide-sm">
                        <span className={`po-badge ${user.role === "Manager" ? "info" : "muted"} um-role`}>{getRoleLabel(user.role)}</span>
                      </td>
                      <td className="po-hide-md">
                        {user.workshops && user.workshops.length > 0 ? (
                          <div className="um-chips">
                            {user.workshops.slice(0, 3).map((w) => (
                              <span key={w.workshopId} className="um-chip">{w.name}</span>
                            ))}
                            {user.workshops.length > 3 && (
                              <span className="um-chip more" title={user.workshops.slice(3).map((w) => w.name).join(", ")}>+{user.workshops.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="um-dash">—</span>
                        )}
                      </td>
                      <td>
                        <div className="um-status">
                          <span className={`po-badge ${user.isActive ? "ok" : "muted"}`}>{user.isActive ? "Aktif" : "Pasif"}</span>
                          {user.isActive && user.isFirstLogin && (
                            <span className="po-badge warn" title="Geçici şifreyle henüz giriş yapmadı">İlk giriş bekliyor</span>
                          )}
                        </div>
                      </td>
                      <td className="act">
                        <div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
                          {canUpdate && (
                            <button type="button" className="po-ib" title="Düzenle" aria-label={`${name} düzenle`} onClick={() => openEdit(user)}>{I.edit}</button>
                          )}
                          {canUpdate && (
                            <button
                              type="button"
                              className="po-ib"
                              aria-label={`${name} için yeni geçici şifre`}
                              onClick={() => setResetTarget(user)}
                              disabled={isSelf || !user.isActive || resetting}
                              title={
                                isSelf
                                  ? "Kendi şifrenizi profilinizden değiştirin"
                                  : !user.isActive
                                    ? "Pasif hesaba şifre üretilmez"
                                    : "Yeni geçici şifre üret ve gönder"
                              }
                            >
                              {I.key}
                            </button>
                          )}
                          {has("user-management:delete") && (
                            <button
                              type="button"
                              className="po-ib danger"
                              aria-label={`${name} sil`}
                              onClick={() => handleDelete(user)}
                              disabled={isSelf}
                              title={isSelf ? "Kendi hesabınızı silemezsiniz" : "Kullanıcıyı sil"}
                            >
                              {I.trash}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Ekle / düzenle modalı */}
      {isModalOpen && (
        <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) closeModal(); }}>
          <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="um-modal-title" onSubmit={handleSubmit}>
            <div className="po-modal-head">
              <div>
                <span className="po-kicker">Kullanıcı</span>
                <h3 id="um-modal-title">{isEdit ? `${editingUser?.firstName ?? ""} ${editingUser?.lastName ?? ""}`.trim() || "Kullanıcıyı düzenle" : "Yeni kullanıcı"}</h3>
              </div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeModal} disabled={saving}>{I.close}</button>
            </div>

            <div className="po-modal-body">
              <div className="po-form-sec">
                <h4>Kişisel bilgiler</h4>
                <div className="po-grid2">
                  <label className="po-field">
                    <span>Ad<em>zorunlu</em></span>
                    <input type="text" value={formData.firstName} autoFocus onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} placeholder="Ad" required />
                  </label>
                  <label className="po-field">
                    <span>Soyad<em>zorunlu</em></span>
                    <input type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} placeholder="Soyad" required />
                  </label>
                  <label className="po-field span2">
                    <span>E-posta<em>zorunlu · giriş adı</em></span>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="ornek@firma.com.tr" required />
                  </label>
                </div>
              </div>

              <div className="po-form-sec">
                <h4>Yetki ve atölye</h4>
                <label className="po-field">
                  <span>Rol<em>zorunlu</em></span>
                  <select className="po-select" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} disabled={isEdit && !isManager} required>
                    <option value="">Rol seçiniz</option>
                    {apiRoles.map((r) => (
                      <option key={r.roleId} value={r.name}>{getRoleLabel(r.name)}</option>
                    ))}
                  </select>
                  {isEdit && !isManager && <span className="po-fieldnote">Sadece müdür rol değiştirebilir.</span>}
                </label>
                <div className="po-field">
                  <span>Atölyeler{workshopRequired && <em>bu rol için en az bir atölye</em>}</span>
                  {workshops.length === 0 ? (
                    <div className="po-blank small"><b>Tanımlı atölye yok</b></div>
                  ) : (
                    <div className="um-ws">
                      <div className="um-ws-bar">
                        <span>{formData.workshopIds.length > 0 ? <><b>{formData.workshopIds.length}</b> / {workshops.length} atölye seçili</> : "Atölye seçilmedi"}</span>
                        <button type="button" className="po-link" onClick={toggleAllWorkshops}>
                          {formData.workshopIds.length === workshops.length ? "Temizle" : "Tümünü seç"}
                        </button>
                      </div>
                      <div className="um-ws-grid">
                        {workshops.map((workshop) => {
                          const checked = formData.workshopIds.includes(workshop.workshopId);
                          return (
                            <label key={workshop.workshopId} className={`po-check um-ws-item ${checked ? "on" : ""}`}>
                              <input type="checkbox" checked={checked} onChange={(e) => toggleWorkshop(workshop.workshopId, e.target.checked)} />
                              <span>{workshop.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="po-form-sec">
                <h4>Güvenlik</h4>
                {!isEdit && (
                  <div className="um-pwmode" role="radiogroup" aria-label="Şifre">
                    <label className={formData.passwordMode === "generate" ? "on" : ""}>
                      <input type="radio" name="um-pwmode" checked={formData.passwordMode === "generate"} onChange={() => setFormData({ ...formData, passwordMode: "generate", password: "" })} />
                      <span><b>Geçici şifre üret ve gönder</b><small>Sistem üretir, kişinin e-postasına gönderir ve size bir kez gösterir; kişi ilk girişte değiştirir.</small></span>
                    </label>
                    <label className={formData.passwordMode === "manual" ? "on" : ""}>
                      <input type="radio" name="um-pwmode" checked={formData.passwordMode === "manual"} onChange={() => setFormData({ ...formData, passwordMode: "manual" })} />
                      <span><b>Şifreyi ben belirleyeyim</b><small>En az 6 karakter; kişi ilk girişte yine değiştirir.</small></span>
                    </label>
                  </div>
                )}
                {(isEdit || formData.passwordMode === "manual") && (
                  <label className="po-field">
                    <span>{isEdit ? "Yeni şifre" : "Şifre"}<em>{isEdit ? "isteğe bağlı" : "zorunlu · en az 6 karakter"}</em></span>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={isEdit ? "Değiştirmek istemiyorsanız boş bırakın" : "En az 6 karakter"}
                      required={!isEdit}
                      minLength={isEdit ? undefined : 6}
                      autoComplete="new-password"
                    />
                    {isEdit && (
                      <span className="po-fieldnote">Şifre değiştirmek istemiyorsanız boş bırakın. Yeni geçici şifre için listedeki anahtar düğmesini kullanın.</span>
                    )}
                  </label>
                )}

                {isEdit && isManager && (
                  <label className="um-active">
                    <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                    <span>
                      <b>Aktif kullanıcı</b>
                      <small>{formData.isActive ? "Kullanıcı sisteme giriş yapabilir" : "Pasif kullanıcı sisteme giriş yapamaz"}</small>
                    </span>
                  </label>
                )}
              </div>
            </div>

            <div className="po-modal-foot">
              <button type="button" className="po-btn" onClick={closeModal} disabled={saving}>Vazgeç</button>
              <button type="submit" className="po-btn primary" disabled={saving}>
                {saving ? "Kaydediliyor..." : isEdit ? "Kaydet" : "Kullanıcıyı ekle"}
              </button>
            </div>
          </form>
        </div>
      )}

      {loginResult && (
        <LoginCredentialsModal who={loginResult.who} result={loginResult.r} reset={loginResult.reset} onClose={() => setLoginResult(null)} />
      )}

      <ConfirmModal
        isOpen={!!resetTarget}
        title="Yeni geçici şifre"
        message={`"${resetTarget?.firstName ?? ""} ${resetTarget?.lastName ?? ""}" için yeni geçici şifre üretilecek; eski şifre geçersiz olur, kişi ilk girişte yenisini belirler. Devam edilsin mi?`}
        type="warning"
        confirmText="Üret ve gönder"
        cancelText="Vazgeç"
        onConfirm={handleConfirmReset}
        onCancel={() => setResetTarget(null)}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Kullanıcıyı sil"
        message={`"${confirmModal.userName}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        type="danger"
        confirmText="Evet, sil"
        cancelText="Vazgeç"
        onConfirm={handleConfirmDelete}
        onCancel={() =>
          setConfirmModal({ isOpen: false, userId: null, userName: "" })
        }
      />
    </div>
  );
};

export default UserManagement;
